import React, { useRef, useState } from 'react'
import {
  Button,
  Input,
  Notification,
  NotificationType,
  Content,
  Textarea
} from '@ampcore/adel-react'
import { Spacer } from '@ampcore/design-system'
import xss from 'xss'
import {
  isValidEmail,
  formatPhoneNumber,
  isValidPhoneNumber
} from '../../../common/utils'
import { formSelectedData, CONSTs } from '../views/utils'
import '../views/index.css'

export type WFWFieldName =
  | 'firstname'
  | 'lastname'
  | 'company'
  | 'employeesize'
  | 'emailaddress'
  | 'phone'
  | 'inquiry'

export type WFWFormData = {
  firstname: string | null
  lastname: string | null
  company: string | null
  employeesize: string | null
  emailaddress: string | null
  phone: string | null
  inquiry: string | null
}

export type WFWErrorFields = {
  firstname: boolean
  lastname: boolean
  company: boolean
  employeesize: boolean
  emailaddress: boolean
  phone: boolean
  inquiry: boolean
  emailWrongFormat: boolean
}

export type WFWFormLabels = {
  firstname: string
  lastname: string
  company: string
  employeesize: string
  emailaddress: string
  phone: string
  inquiry: string
  buttonlabel: string
  buttonLoadingLabel: string
}

export type WFWFormContent = {
  heading: string
  subheading: string
  disclosure: string
}

export type WFWFormEmailInfo = {
  emailApi: string
  emailFormHeading: string
  emailGreeting: string
  emailIntro: string
  subject: string
  toEmailAddress: string
}

export type WFWFormErrorMessages = {
  errorFirstname: string
  errorLastname: string
  errorCompany: string
  errorEmailaddress: string
  errorEmailaddressWrongFormat: string
  errorPhone: string
}

export type WFWServiceMessages = {
  serviceSuccess: string
  serviceSuccessThankyou: string
  serviceFailed: string
}

export type WFWFormPleaseNote = {
  message: string
}

export type WFWShowServerResponse = (props: unknown, message?: string) => void

export type ContactFormWorkplaceFinancialWellnessContent = {
  componentSpacing?: React.ComponentProps<typeof Spacer>['spacing']
  formLabels: WFWFormLabels
  formContent: WFWFormContent
  formEmailInfo: WFWFormEmailInfo
  formErrorMessages: WFWFormErrorMessages
  serviceMessages?: Partial<WFWServiceMessages>
  formPleaseNote?: WFWFormPleaseNote
  formTemplate?: string
}

export type ContactFormWFWCallback = (
  items: unknown,
  emailInfo: WFWFormEmailInfo,
  showServerResponse: WFWShowServerResponse,
  showEmailIntro: boolean
) => unknown

export type ContactFormWorkplaceFinancialWellnessProps = {
  callback?: ContactFormWFWCallback
  name?: string
  content: ContactFormWorkplaceFinancialWellnessContent
  testId?: string
}

const ContactFormWorkplaceFinancialWellness = (
  props: ContactFormWorkplaceFinancialWellnessProps
) => {
  const { callback, name, content, testId } = props

  const [formData, setFormData] = useState<WFWFormData>({
    firstname: null,
    lastname: null,
    company: null,
    employeesize: null,
    emailaddress: null,
    phone: null,
    inquiry: null
  })

  const [errorFields, setErrorFields] = useState<WFWErrorFields>({
    firstname: false,
    lastname: false,
    company: false,
    employeesize: false,
    emailaddress: false,
    phone: false,
    inquiry: false,
    emailWrongFormat: false
  })

  const phoneRef = useRef<HTMLInputElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [showServiceFailedMessage, setShowServiceFailedMessage] =
    useState(false)

  const validateField = (fieldName: WFWFieldName, value: string) => {
    const fieldErrors: WFWErrorFields = { ...errorFields }
    const updatedFormData: WFWFormData = { ...formData }
    const isNotEmpty = value.trim().length > 0

    switch (fieldName) {
    case 'firstname': {
      fieldErrors.firstname = !isNotEmpty
      updatedFormData.firstname = value.trim() || null
      break
    }
    case 'lastname': {
      fieldErrors.lastname = !isNotEmpty
      updatedFormData.lastname = value.trim() || null
      break
    }
    case 'company': {
      fieldErrors.company = !isNotEmpty
      updatedFormData.company = value.trim() || null
      break
    }
    case 'emailaddress': {
      const isEmailValid = isValidEmail(value)
      fieldErrors.emailaddress = !isEmailValid
      fieldErrors.emailWrongFormat = !isEmailValid && isNotEmpty
      updatedFormData.emailaddress = fieldErrors.emailaddress ? null : value
      break
    }
    case 'phone': {
      const isValidPhone = isValidPhoneNumber(value, true)
      fieldErrors.phone = !isValidPhone
      if (isValidPhone) {
        const formattedPhone = formatPhoneNumber(value)
        if (phoneRef.current) {
          phoneRef.current.value = formattedPhone
        }
        updatedFormData.phone = formattedPhone
      } else {
        updatedFormData.phone = null
      }
      break
    }
    case 'employeesize': {
      updatedFormData.employeesize = value || null
      break
    }
    case 'inquiry': {
      updatedFormData.inquiry = value || null
      break
    }
    }

    setFormData(updatedFormData)
    setErrorFields(fieldErrors)
  }

  const handleFieldChange = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null
    if (!target || !target.name) return
    validateField(target.name as WFWFieldName, target.value || '')
  }

  const isFormComplete = Object.entries(formData).every(([key, value]) => {
    if (key === 'employeesize' || key === 'phone' || key === 'inquiry')
      return true
    return value !== null
  })

  const showServerResponse: WFWShowServerResponse = (props, message) => {
    if (props && message) {
      if (message === CONSTs.SUCCESS) {
        setShowSuccessMessage(true)
        setShowServiceFailedMessage(false)
      } else {
        setShowSuccessMessage(false)
        setShowServiceFailedMessage(true)
      }
      setLoading(false)
    }
  }

  const submitForm = (event: { preventDefault: () => void }) => {
    event.preventDefault()

    if (!isFormComplete) {
      const fieldErrors: WFWErrorFields = { ...errorFields }
      fieldErrors.firstname = !formData.firstname
      fieldErrors.lastname = !formData.lastname
      fieldErrors.company = !formData.company
      fieldErrors.emailaddress = !isValidEmail(formData.emailaddress || '')
      fieldErrors.phone = !isValidPhoneNumber(formData.phone, true)
      setErrorFields(fieldErrors)
      return
    }

    setLoading(true)
    const items = formSelectedData(formData, content.formTemplate)
    if (callback && typeof callback === 'function' && items) {
      const showEmailIntroMessage = true
      const { emailGreeting, emailIntro, emailFormHeading } =
        content.formEmailInfo
      const updatedEmailInfo: WFWFormEmailInfo = {
        ...content.formEmailInfo,
        emailIntro: `${emailGreeting},<br>${emailIntro}<br><br><b>${emailFormHeading}</b><br>`
      }
      callback(
        items,
        updatedEmailInfo,
        showServerResponse,
        showEmailIntroMessage
      )
    }
  }

  const setEmailErrorMessage = (
    wrongEmail: boolean,
    message: WFWFormErrorMessages
  ): string => {
    return wrongEmail
      ? message.errorEmailaddressWrongFormat
      : message.errorEmailaddress
  }

  const successMessage = (
    <>
      <h3 className="u-paddingBottom16">
        {content.serviceMessages?.serviceSuccessThankyou}
      </h3>
      <p
        className="u-paddingTop16 u-paddingBottom16"
        dangerouslySetInnerHTML={{
          __html: xss(content.serviceMessages?.serviceSuccess ?? '')
        }}
      />
    </>
  )

  return (
    <Spacer spacing={content.componentSpacing}>
      {showSuccessMessage && content.serviceMessages?.serviceSuccess && (
        <Notification
          type={NotificationType.CONFIRM}
          dismissable={false}
          className="WFWFormNotification u-sizeConstrained"
          children={successMessage}
        />
      )}
      {!showSuccessMessage && (
        <div className="u-sizeConstrained">
          <div className="u-lg-size8of12 u-lg-before2of12 u-size10of12 u-md-before1of12 u-sm-sizeFull u-paddingBottom8">
            <h3 className="u-textDefault u-textCenter u-textSecondary u-textNormal u-paddingBottom40">
              {content.formContent?.heading}
            </h3>
            <div className="u-bgColorLight">
              <div className="u-paddingHoriz40 u-paddingTop40">
                <h4
                  className="u-textDefault u-textColorBrand u-textSecondary u-textNormal u-lg-textSizeMsrp1 u-md-textSizeMsrp2 u-sm-textSizeMsrp1 u-textCenter"
                  dangerouslySetInnerHTML={{
                    __html: xss(content.formContent?.subheading)
                  }}
                />
              </div>
              <div>
                <form
                  name={name}
                  className="u-padding40"
                  onBlur={handleFieldChange}
                  onKeyUp={handleFieldChange}
                >
                  <hr />
                  <div className="u-paddingTop24">
                    <div className="u-flex u-flexRow u-sm-flexCol">
                      <div className="u-size6of12 u-sm-sizeFull">
                        <Input
                          name="firstname"
                          id="firstname"
                          label={content.formLabels.firstname}
                          error={errorFields.firstname}
                          message={content.formErrorMessages.errorFirstname}
                          maxLength={100}
                          testId={testId ? `${testId}-firstname` : undefined}
                        />
                      </div>
                      <div className="u-size6of12 u-lg-before1of12 u-md-before1of12 u-sm-sizeFull">
                        <Input
                          name="lastname"
                          id="lastname"
                          label={content.formLabels.lastname}
                          error={errorFields.lastname}
                          message={content.formErrorMessages.errorLastname}
                          maxLength={100}
                          testId={testId ? `${testId}-lastname` : undefined}
                        />
                      </div>
                    </div>
                    <div className="u-flex u-flexRow u-sm-flexCol">
                      <div className="u-size6of12 u-sm-sizeFull">
                        <Input
                          name="company"
                          id="company"
                          label={content.formLabels.company}
                          error={errorFields.company}
                          message={content.formErrorMessages.errorCompany}
                          maxLength={100}
                          testId={testId ? `${testId}-company` : undefined}
                        />
                      </div>
                      <div className="u-size6of12 u-lg-before1of12 u-md-before1of12 u-sm-sizeFull">
                        <Input
                          name="employeesize"
                          id="employeesize"
                          label={content.formLabels.employeesize}
                          testId={testId ? `${testId}-employeesize` : undefined}
                        />
                      </div>
                    </div>
                    <div className="u-flex u-flexRow u-sm-flexCol">
                      <div className="u-size6of12 u-sm-sizeFull">
                        <Input
                          name="emailaddress"
                          id="email"
                          label={content.formLabels.emailaddress}
                          error={errorFields.emailaddress}
                          message={setEmailErrorMessage(
                            errorFields.emailWrongFormat,
                            content.formErrorMessages
                          )}
                          testId={testId ? `${testId}-email` : undefined}
                        />
                      </div>
                      <div className="u-size6of12 u-lg-before1of12 u-md-before1of12 u-sm-sizeFull">
                        <Input
                          name="phone"
                          id="phone"
                          label={content.formLabels.phone}
                          reference={phoneRef}
                          error={errorFields.phone}
                          message={content.formErrorMessages.errorPhone}
                          testId={testId ? `${testId}-phone` : undefined}
                        />
                      </div>
                    </div>
                    <div className="u-flex u-flexRow u-sm-flexCol">
                      <div className="u-sizeFull">
                        <Textarea
                          name="inquiry"
                          id="inquiry"
                          label={content.formLabels.inquiry}
                          row={3}
                          maxLength={250}
                          testId={testId ? `${testId}-inquiry` : undefined}
                        />
                      </div>
                    </div>
                    <div
                      className="Disclaimer-text u-marginBottom24"
                      dangerouslySetInnerHTML={{
                        __html: xss(content.formContent?.disclosure)
                      }}
                    />
                  </div>
                  {showServiceFailedMessage &&
                    content.serviceMessages?.serviceFailed && (
                    <Notification
                      type={NotificationType.ALERT}
                      message={content.serviceMessages.serviceFailed}
                      dismissable={false}
                      className="u-marginBottom24"
                    />
                  )}
                  <Button
                    onClick={submitForm}
                    isLoading={loading}
                    data-analytics={'Button'}
                    data-tracking={'Button'}
                    children={
                      loading
                        ? content.formLabels.buttonLoadingLabel
                        : content.formLabels.buttonlabel
                    }
                  />
                </form>
              </div>
            </div>
            {content.formPleaseNote?.message && (
              <Content className={'u-marginBottom24'}>
                <div
                  className="Content"
                  dangerouslySetInnerHTML={{
                    __html: xss(content.formPleaseNote.message)
                  }}
                />
              </Content>
            )}
          </div>
        </div>
      )}
    </Spacer>
  )
}

export default ContactFormWorkplaceFinancialWellness
